import { Pressable, StyleSheet, Text, View, type LayoutChangeEvent } from "react-native";
import { useTranslation } from "react-i18next";
import {
  bandLabel,
  formatDraftGridCell,
  formatDraftWeekLine,
  programRoundLabel,
  rectHas,
  slotRoundBand,
  type CellPos,
  type CellRect,
  type CurrentMax,
  type DraftGridLens,
  type DraftLineOptions,
  type RoundType,
  type SlotDraft,
  type UnitsPreference,
} from "@heraion/shared";

import type { GridGestureBundle } from "../IntensityGrid/useGridGestures";
import type { useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { UiTheme } from "../theme";
import { LINE, RING, ROW_MIN, RoundGridCell, type CellFieldSpec } from "./RoundGridCell";
import { gridCellId } from "./gridKeyContract";

/** The least height of the head row (over the 44pt tap floor). */
export const HEAD_MIN = 48;

/** What every round column of the day grid reads. */
export type ColumnCtx = {
  theme: UiTheme;
  slots: readonly SlotDraft[];
  lens: DraftGridLens;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  resolveName: (id: string, snapshot?: string) => string;
  /** The rounds by hand of each exercise. */
  hand: readonly ReadonlySet<number>[];
  words: ReturnType<typeof useRoundTypeWords>;
  typeOf: (w: number) => RoundType;
  headH: number;
  heightOf: (id: string, min: number) => number;
  report: (key: string, extra: number) => (e: LayoutChangeEvent) => void;
  open: CellPos | null;
  /** The selected block, or the one a fill would cover. */
  range: CellRect | null;
  /** Where the fill handle sits, when there is one. */
  handleAt: CellPos | null;
  /** The open cell's field, while it is open; `name` and `round` name it. */
  fieldAt: (p: CellPos, name: string, round: string) => CellFieldSpec | undefined;
  onCell: (p: CellPos) => void;
  /** A cell's hold and screen-reader actions. */
  cellGesture: (p: CellPos) => GridGestureBundle;
  onHead: (w: number) => void;
};

const at = (p: CellPos | null, row: number, col: number) => !!p && p.row === row && p.col === col;

// One round of the wide day grid (#336 slice 6): its head, which opens the
// round menu, and a cell per exercise. Out of ExerciseRoundGrid for its size.
export function RoundColumn({ ctx, w, styles: g }: { ctx: ColumnCtx; w: number; styles: ColumnStyles }) {
  const { t } = useTranslation();
  const off = t("editorV3.notInRound", { defaultValue: "Not in this round" });
  const handWord = t("editorV3.rule.byHandMark", { defaultValue: "by hand" });
  const type = ctx.typeOf(w);
  const marked = type !== "normal";
  const label = programRoundLabel(w + 1, type, ctx.words);
  return (
    <View style={[g.column, marked && type === "deload" ? g.deload : null]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("editorV3.grid.roundHeadA11y", { round: label, defaultValue: "{{round}}, round menu" })}
        onPress={() => ctx.onHead(w)}
        style={({ pressed }) => [g.head, g.roundHead, { height: ctx.headH }, pressed && g.pressed]}
      >
        <View onLayout={ctx.report(`head:${w}`, LINE)} style={g.headContent}>
          <Text style={g.round}>{programRoundLabel(w + 1)}</Text>
          <Text style={g.type}>{marked ? ctx.words[type] : " "}</Text>
        </View>
      </Pressable>
      {ctx.slots.map((slot, slotIdx) => {
        const name = ctx.resolveName(slot.exerciseId, slot.exerciseName);
        const max = ctx.maxMeta?.get(slot.exerciseId);
        const text = formatDraftGridCell(slot, w, ctx.lens, ctx.lineOptions, max);
        const band = slotRoundBand(slot, w, { max, cellUnit: ctx.cellUnit })?.band ?? null;
        const byHand = text !== null && ctx.hand[slotIdx]!.has(w);
        const line = formatDraftWeekLine(slot, w, ctx.lineOptions);
        const spoken = [
          t("editorV3.grid.cellA11y", { name, round: label, line: line ?? off, defaultValue: "{{name}}, {{round}}: {{line}}" }),
          band ? bandLabel(t, band) : null,
          byHand ? handWord : null,
        ]
          .filter(Boolean)
          .join(", ");
        const pos = { row: slotIdx, col: w };
        const open = at(ctx.open, slotIdx, w);
        return (
          <RoundGridCell
            key={slot.id}
            theme={ctx.theme}
            id={gridCellId(pos)}
            cell={{ text, band, byHand, spoken, open, inRange: !!ctx.range && rectHas(ctx.range, pos) }}
            height={ctx.heightOf(slot.id, ROW_MIN)}
            onLayout={ctx.report(`${slot.id}:${w}`, LINE + 2 * RING)}
            onPress={() => ctx.onCell(pos)}
            gesture={ctx.cellGesture(pos)}
            field={open && text !== null ? ctx.fieldAt(pos, name, label) : undefined}
            fillHandle={at(ctx.handleAt, slotIdx, w)}
          />
        );
      })}
    </View>
  );
}

export type ColumnStyles = ReturnType<typeof makeColumnStyles>;

/** The round columns' styles; the grid's own head cells take `head` too. */
export function makeColumnStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    column: { minWidth: 72, flexGrow: 1 },
    deload: { backgroundColor: p.surface2 },
    head: { minHeight: HEAD_MIN, justifyContent: "center", borderBottomWidth: 1, borderBottomColor: p.border },
    roundHead: { alignItems: "center", paddingHorizontal: s.s1 },
    headContent: { alignItems: "center", paddingVertical: s.s1 },
    round: { color: p.ink2, fontSize: ty.sizes.sm, fontFamily: ty.fonts.mono, fontWeight: "700" },
    type: { color: p.ink3, fontSize: ty.sizes.sm, textAlign: "center" },
    pressed: { backgroundColor: p.surface2 },
  });
}
